import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router'
import { rpc } from './api/rpc'
import { session, useSession } from './api/session'
import { AppShell } from './components/AppShell'
import { Spinner } from './components/ui/Spinner'
import { ToastProvider } from './components/ui/Toast'
import { ConfirmHost } from './components/ui/Confirm'
import { InvitePage, LoginPage, SetupPage, SignupPage } from './pages/AuthPages'
import { AutomationEditor, AutomationsList } from './pages/AutomationsPage'
import { ChatPage } from './pages/ChatPage'
import { RunPage } from './pages/RunPage'
import { FilesPage } from './pages/FilesPage'
import { InboxPage } from './pages/InboxPage'
import { SettingsPage } from './pages/SettingsPage'

function RedirectKeepSearch({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

function AdminRedirect() {
  const { tab } = useParams()
  return <Navigate to={`/settings/${tab === 'settings' ? 'server' : tab ?? 'users'}`} replace />
}

function Routed() {
  const s = useSession()
  const { i18n } = useTranslation()

  useEffect(() => { void session.bootstrap() }, [])
  // One socket while signed in; re-authenticate it whenever the access token rotates.
  useEffect(() => {
    if (s.status === 'authenticated') { rpc.start(); rpc.reauth() } else rpc.stop()
  }, [s.status, s.token])
  useEffect(() => {
    if (s.user && i18n.language !== s.user.locale) void i18n.changeLanguage(s.user.locale)
  }, [s.user, i18n])

  if (s.status === 'loading') {
    return <div className="flex h-full items-center justify-center text-ink-500"><Spinner /></div>
  }
  if (s.status === 'anonymous') {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/setup" element={s.needsSetup ? <SetupPage /> : <Navigate to="/login" replace />} />
        <Route path="/login" element={s.needsSetup ? <Navigate to="/setup" replace /> : <LoginPage openRegistration={s.openRegistration} sso={s.sso} />} />
        {s.openRegistration && <Route path="/signup" element={<SignupPage />} />}
        <Route path="*" element={<Navigate to={s.needsSetup ? '/setup' : '/login'} replace />} />
      </Routes>
    )
  }
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<ChatPage />} />
        <Route path="chat/:chatId" element={<ChatPage />} />
        <Route path="automations" element={<AutomationsList />} />
        <Route path="automations/new" element={<AutomationEditor key="new" />} />
        <Route path="automations/runs/:runId" element={<RunPage />} />
        <Route path="automations/:routineId" element={<AutomationEditor />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="files" element={<FilesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/:section" element={<SettingsPage />} />
        {/* Old locations of what now lives in Settings. */}
        <Route path="connectors" element={<RedirectKeepSearch to="/settings/connectors" />} />
        <Route path="teams" element={<Navigate to="/settings/teams" replace />} />
        <Route path="knowledge" element={<Navigate to="/settings/knowledge" replace />} />
        <Route path="usage" element={<Navigate to="/settings/usage" replace />} />
        <Route path="admin" element={<Navigate to="/settings/users" replace />} />
        <Route path="admin/settings" element={<Navigate to="/settings/server" replace />} />
        <Route path="admin/:tab" element={<AdminRedirect />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <Routed />
        <ConfirmHost />
      </BrowserRouter>
    </ToastProvider>
  )
}
